import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { fmtFecha } from '../utils/dates';
import {
  RefreshCw, X, Search, MessageSquare, CheckCircle, Clock, PlayCircle,
  BarChart2, Send, FileText, AlertCircle,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';

// ─── Catálogo de resultados de una renovación ───
const OUTCOMES = {
  renueva_igual:   { label: 'Renueva en iguales condiciones', color: '#34d399' },
  renueva_aumento: { label: 'Renueva con aumento de cobertura', color: '#4d8fff' },
  renueva_baja:    { label: 'Renueva con baja de cobertura', color: '#8ab4ff' },
  endoso:          { label: 'Endoso de póliza', color: '#f5a623' },
  cancel_venta:    { label: 'Cancelación por venta de vehículo', color: '#ff4d6d' },
  cancel_corredor: { label: 'Cancelación por cambio de corredor', color: '#c94d6d' },
  cancel_deuda:    { label: 'Cancelación por deuda', color: '#993355' },
};

const STATUS = {
  pending:     { label: 'Pendiente', class: 'badge-amber', icon: Clock },
  in_progress: { label: 'En proceso', class: 'badge-blue', icon: PlayCircle },
  done:        { label: 'Finalizada', class: 'badge-green', icon: CheckCircle },
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  ...(localStorage.getItem('nebb_token')
    ? { Authorization: `Bearer ${localStorage.getItem('nebb_token')}` }
    : {}),
});

const monthKey = (dateStr) => (dateStr || '').slice(0, 7); // 'YYYY-MM'

// ─── Panel lateral (estilo Siniestros) ───
function RenewalPanel({ renewal, onClose, onUpdate, onAddNote }) {
  const { clients, policies, users, currentUser } = useApp();
  const navigate = useNavigate();
  const policyReal = policies.find(p => p.id === renewal.policyId);
  const policy = policyReal || (renewal.policyNumber ? { number: renewal.policyNumber, company: renewal.company } : {});
  const policyExiste = !!policyReal;
  // El cliente sale de la PÓLIZA ACTUAL (si fue reasignada, acá se refleja);
  // recién después caemos al clientId histórico de la ficha o al snapshot.
  const client = clients.find(c => c.id === (policyReal?.clientId || renewal.clientId))
    || (renewal.clientName ? { name: renewal.clientName } : {});
  const [noteText, setNoteText] = useState('');
  const [sending, setSending] = useState(false);

  const enviarNota = async () => {
    if (!noteText.trim()) return;
    setSending(true);
    await onAddNote(renewal.id, noteText.trim());
    setNoteText('');
    setSending(false);
  };

  return (
    <div className="side-panel" style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 240, zIndex: 1000, display: 'flex', height: '100vh' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
      <div style={{
        marginLeft: 'auto', width: 'min(560px, 100%)', height: '100%', position: 'relative', zIndex: 1,
        background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexShrink: 0 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>
              {client.name || '(cliente sin ficha)'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              Póliza {policy.number || '—'} · {policy.company || '—'} · Vence {fmtFecha(renewal.dueDate)}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {policyExiste && (
              <button className="btn btn-primary btn-sm" onClick={() => navigate(`/policies/${renewal.policyId}?renovar=1`)}>
                <RefreshCw size={13} /> Renovar
              </button>
            )}
            {policyExiste && (
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/policies/${renewal.policyId}`)}>
                <FileText size={13} /> Ver póliza
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ padding: '6px 8px' }}><X size={14} /></button>
          </div>
        </div>

        {/* Cuerpo scrolleable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Estado y asignación */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {Object.entries(STATUS).map(([key, s]) => (
                <button
                  key={key}
                  className={`btn btn-sm ${renewal.status === key ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => onUpdate(renewal.id, { status: key })}
                >
                  <s.icon size={13} /> {s.label}
                </button>
              ))}
            </div>
            <div className="form-group">
              <label className="form-label">Asignado a</label>
              <select
                value={renewal.assignedTo || ''}
                onChange={e => onUpdate(renewal.id, { assignedTo: e.target.value })}
              >
                <option value="">Sin asignar</option>
                {users.map(u => (
                  <option key={u.id} value={u.name || u.username}>{u.name || u.username}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Fecha de vencimiento {renewal.dueDateManual && <span style={{ fontSize: 10, color: 'var(--accent)' }}>(ajustada a mano)</span>}</label>
              <input
                type="date"
                value={renewal.dueDate || ''}
                onChange={e => e.target.value && onUpdate(renewal.id, { dueDate: e.target.value })}
              />
              <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 4 }}>
                Determina en qué mes aparece esta ficha. Si la corregís a mano, la sincronización no la vuelve a cambiar.
              </div>
            </div>
          </div>

          {/* Resultado final */}
          <div className="card">
            <div style={{ fontSize: 12, fontFamily: 'var(--font-display)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
              Resultado de la renovación
            </div>
            <select
              value={renewal.outcome || ''}
              onChange={e => e.target.value && onUpdate(renewal.id, { outcome: e.target.value })}
            >
              <option value="">— Elegir al finalizar —</option>
              {Object.entries(OUTCOMES).map(([key, o]) => (
                <option key={key} value={key}>{o.label}</option>
              ))}
            </select>
            {renewal.outcome && (
              <div style={{ marginTop: 10, fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 10, height: 10, borderRadius: 5, background: OUTCOMES[renewal.outcome]?.color, display: 'inline-block' }} />
                {OUTCOMES[renewal.outcome]?.label}
                {renewal.closedAt && <span style={{ color: 'var(--text-muted)' }}> · cerrada {fmtFecha(renewal.closedAt.slice(0, 10))}</span>}
              </div>
            )}
          </div>

          {/* Historial de comunicación */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, fontFamily: 'var(--font-display)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <MessageSquare size={13} /> Historial de comunicación
            </div>

            {(renewal.notes || []).length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '8px 0' }}>
                Sin notas todavía. Registrá acá cada llamada, mail o WhatsApp con el cliente, para que quien tome la ficha tenga todos los antecedentes.
              </div>
            )}

            {(renewal.notes || []).map(n => (
              <div key={n.id} style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                  <strong style={{ color: 'var(--text-secondary)' }}>{n.author}</strong> · {new Date(n.at).toLocaleString('es-UY', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{n.text}</div>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea
                rows={2}
                placeholder={`Agregar nota como ${currentUser?.name || 'usuario'}...`}
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                style={{ flex: 1, resize: 'vertical' }}
              />
              <button className="btn btn-primary btn-sm" disabled={sending || !noteText.trim()} onClick={enviarNota}>
                <Send size={13} />
              </button>
            </div>
          </div>

          {/* Datos de contacto rápidos */}
          <div className="card">
            <div style={{ fontSize: 12, fontFamily: 'var(--font-display)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
              Contacto
            </div>
            <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span>📞 {client.phone || '—'}</span>
              <span>✉️ {client.email || '—'}</span>
              <span><FileText size={12} style={{ verticalAlign: '-2px' }} /> {policy.type || '—'} · Prima: {policy.premium ? `${policy.currency === 'USD' ? 'U$S' : '$'} ${Number(policy.premium).toLocaleString('es-UY')}` : '—'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Informe (solo administrador) ───
function ReportModal({ renewals, monthLabel, onClose }) {
  const { users } = useApp();

  const done = renewals.filter(r => r.status === 'done' && r.outcome);
  const byOutcome = Object.entries(OUTCOMES)
    .map(([key, o]) => ({ name: o.label, value: done.filter(r => r.outcome === key).length, color: o.color }))
    .filter(x => x.value > 0);

  const byAgent = {};
  done.forEach(r => {
    const a = r.assignedTo || 'Sin asignar';
    byAgent[a] = (byAgent[a] || 0) + 1;
  });
  const agentData = Object.entries(byAgent).map(([name, cerradas]) => ({ name, cerradas }));

  const total = renewals.length;
  const pendientes = renewals.filter(r => r.status === 'pending').length;
  const enProceso = renewals.filter(r => r.status === 'in_progress').length;
  const renuevan = done.filter(r => r.outcome.startsWith('renueva') || r.outcome === 'endoso').length;
  const cancelan = done.filter(r => r.outcome.startsWith('cancel')).length;
  const retencion = done.length ? Math.round((renuevan / done.length) * 100) : 0;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 760 }}>
        <div className="modal-header">
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>Informe de renovaciones — {monthLabel}</h2>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>Solo visible para administrador</div>
          </div>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <div className="modal-body">
          <div className="stats-grid" style={{ marginBottom: 8 }}>
            <div className="stat-card"><span className="stat-label">Total fichas</span><span className="stat-value">{total}</span></div>
            <div className="stat-card"><span className="stat-label">Finalizadas</span><span className="stat-value">{done.length}</span><span className="stat-sub">{pendientes} pendientes · {enProceso} en proceso</span></div>
            <div className="stat-card"><span className="stat-label">Retención</span><span className="stat-value" style={{ color: 'var(--accent)' }}>{retencion}%</span><span className="stat-sub">{renuevan} renuevan/endosan · {cancelan} cancelan</span></div>
          </div>

          {byOutcome.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '24px 0', fontSize: 13 }}>
              Todavía no hay renovaciones finalizadas este mes para graficar.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, fontFamily: 'var(--font-display)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '10px 0 4px' }}>Distribución por resultado</div>
              <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={byOutcome} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ value }) => value}>
                      {byOutcome.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {agentData.length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontFamily: 'var(--font-display)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '14px 0 4px' }}>Fichas cerradas por persona</div>
                  <div style={{ width: '100%', height: 220 }}>
                    <ResponsiveContainer>
                      <BarChart data={agentData}>
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#8892b0' }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#8892b0' }} />
                        <Tooltip />
                        <Bar dataKey="cerradas" fill="#7c6cf8" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ───
export default function RenewalsPage() {
  const { clients, policies, currentUser, showToast } = useApp();
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // ficha abierta en panel
  const [reportOpen, setReportOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mes visible: actual o próximo (no más allá)
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const nextMonth = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(thisMonth);
  const MES = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','setiembre','octubre','noviembre','diciembre'];
  const monthLabel = (m) => `${MES[parseInt(m.slice(5), 10) - 1]} ${m.slice(0, 4)}`;

  const cargar = useCallback(async () => {
    try {
      // Primero sincroniza (alimenta el módulo desde las pólizas cargadas),
      // después trae las fichas.
      await fetch('/api/renewals/sync', { method: 'POST', headers: authHeaders() });
      const res = await fetch('/api/renewals', { headers: authHeaders() });
      const data = await res.json();
      setRenewals(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast('No se pudieron cargar las renovaciones', 'warning');
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { cargar(); }, [cargar]);

  // Enlace directo desde el email diario: /renewals?id=XXX abre esa ficha.
  // Espera a que las renovaciones estén cargadas, ajusta el mes visible al de la
  // ficha (por si no es el mes actual) y la abre en el panel. Corre una sola vez.
  const [deepLinkHecho, setDeepLinkHecho] = useState(false);
  useEffect(() => {
    if (deepLinkHecho || loading) return;
    const id = new URLSearchParams(window.location.search).get('id');
    if (!id) return;
    const ficha = renewals.find(r => r.id === id);
    if (ficha) {
      if (ficha.dueDate) setMonth(monthKey(ficha.dueDate));
      setSelected(ficha);
      window.history.replaceState({}, '', window.location.pathname);
    }
    setDeepLinkHecho(true);
  }, [loading, renewals, deepLinkHecho]);

  const [detectando, setDetectando] = useState(false);
  const detectarFaltantes = async () => {
    setDetectando(true);
    try {
      const res = await fetch('/api/renewals/detect-missing', { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (data.marcadas > 0) {
        showToast(`${data.marcadas} póliza(s) detectada(s) y sumada(s) a Renovaciones`, 'success');
        await cargar(); // re-sincroniza y refresca las fichas
      } else {
        showToast('No se encontraron avisos pendientes sin marcar', 'info');
      }
    } catch (e) {
      showToast('Error al detectar avisos', 'warning');
    }
    setDetectando(false);
  };

  const actualizar = async (id, cambios) => {
    try {
      const res = await fetch(`/api/renewals/${id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(cambios),
      });
      const upd = await res.json();
      if (upd.error) throw new Error(upd.error);
      setRenewals(rs => rs.map(r => (r.id === id ? upd : r)));
      if (selected?.id === id) setSelected(upd);
    } catch (e) {
      showToast('No se pudo actualizar: ' + e.message, 'warning');
    }
  };

  const agregarNota = async (id, text) => {
    try {
      const res = await fetch(`/api/renewals/${id}/notes`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ text }),
      });
      const note = await res.json();
      if (note.error) throw new Error(note.error);
      setRenewals(rs => rs.map(r => (r.id === id ? { ...r, notes: [...(r.notes || []), note] } : r)));
      setSelected(s => (s && s.id === id ? { ...s, notes: [...(s.notes || []), note] } : s));
    } catch (e) {
      showToast('No se pudo guardar la nota', 'warning');
    }
  };

  // Si la póliza/cliente ya no existen (borrados o renovados), usamos el
  // snapshot guardado en la propia ficha para no mostrar "(sin ficha)".
  // El cliente sale de la póliza ACTUAL: si fue reasignada, acá se refleja.
  const getPolicy = (id, r) => policies.find(p => p.id === id) || (r?.policyNumber ? { number: r.policyNumber, company: r.company } : {});
  const getClient = (id, r) => {
    const pReal = policies.find(p => p.id === r?.policyId);
    return clients.find(c => c.id === (pReal?.clientId || id)) || (r?.clientName ? { name: r.clientName } : {});
  };

  // Fichas del mes elegido (actual o próximo, nada más)
  const delMes = useMemo(() => renewals.filter(r => monthKey(r.dueDate) === month), [renewals, month]);

  const filtradas = useMemo(() => {
    const q = search.toLowerCase().trim();
    return delMes
      .filter(r => filterStatus === 'all' || r.status === filterStatus)
      .filter(r => {
        if (!q) return true;
        const c = getClient(r.clientId, r);
        const p = getPolicy(r.policyId, r);
        return (c.name || '').toLowerCase().includes(q)
          || (p.number || '').toLowerCase().includes(q)
          || (p.company || '').toLowerCase().includes(q)
          || (r.assignedTo || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  }, [delMes, search, filterStatus, clients, policies]); // eslint-disable-line

  const stats = {
    total: delMes.length,
    pending: delMes.filter(r => r.status === 'pending').length,
    in_progress: delMes.filter(r => r.status === 'in_progress').length,
    done: delMes.filter(r => r.status === 'done').length,
  };

  // Meses a mostrar en el selector: el actual y el próximo SIEMPRE, más
  // cualquier otro mes que tenga fichas (así ningún volante queda invisible,
  // sea cual sea el mes de inicio de su vigencia nueva).
  const mesesDisponibles = useMemo(() => {
    const set = new Set([thisMonth, nextMonth]);
    (renewals || []).forEach(r => { if (r.dueDate) set.add(monthKey(r.dueDate)); });
    return [...set].sort();
  }, [renewals, thisMonth, nextMonth]);

  return (
    <div className="page fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Renovaciones</h1>
          <p className="page-subtitle">Escritorio de trabajo mensual · se alimenta solo desde las pólizas cargadas</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={cargar}>
            <RefreshCw size={14} /> Actualizar
          </button>
          <button className="btn btn-secondary" onClick={detectarFaltantes} disabled={detectando}>
            {detectando ? 'Detectando...' : <><AlertCircle size={14} /> Detectar avisos faltantes</>}
          </button>
          {currentUser?.role === 'admin' && (
            <button className="btn btn-primary" onClick={() => setReportOpen(true)}>
              <BarChart2 size={14} /> Informe
            </button>
          )}
        </div>
      </div>

      {/* Selector de mes: actual, próximo y todos los que tengan fichas */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {mesesDisponibles.map(m => {
          const cuenta = renewals.filter(r => monthKey(r.dueDate) === m).length;
          return (
            <button
              key={m}
              className={`btn btn-sm ${month === m ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setMonth(m)}
              style={{ textTransform: 'capitalize' }}
            >
              {monthLabel(m)}{cuenta > 0 ? ` (${cuenta})` : ''}
            </button>
          );
        })}
      </div>

      {/* Stats del mes */}
      <div className="stats-grid">
        <div className="stat-card"><span className="stat-label">Vencen en {monthLabel(month)}</span><span className="stat-value">{stats.total}</span></div>
        <div className="stat-card"><span className="stat-label">Pendientes</span><span className="stat-value" style={{ color: 'var(--amber)' }}>{stats.pending}</span></div>
        <div className="stat-card"><span className="stat-label">En proceso</span><span className="stat-value" style={{ color: 'var(--blue)' }}>{stats.in_progress}</span></div>
        <div className="stat-card"><span className="stat-label">Finalizadas</span><span className="stat-value" style={{ color: 'var(--accent)' }}>{stats.done}</span></div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            placeholder="Buscar por cliente, póliza, compañía o asignado..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 34 }}
          />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ width: 170 }}>
          <option value="all">Todos los estados</option>
          <option value="pending">Pendientes</option>
          <option value="in_progress">En proceso</option>
          <option value="done">Finalizadas</option>
        </select>
      </div>

      {/* Lista */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>Cargando...</div>
        ) : filtradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>
            No hay renovaciones {filterStatus !== 'all' ? 'con ese estado ' : ''}para {monthLabel(month)}.
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Póliza</th>
                  <th>Compañía</th>
                  <th>Vence</th>
                  <th>Asignado</th>
                  <th>Estado</th>
                  <th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map(r => {
                  const c = getClient(r.clientId, r);
                  const p = getPolicy(r.policyId, r);
                  const st = STATUS[r.status] || STATUS.pending;
                  return (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setSelected(r)}>
                      <td><div style={{ fontWeight: 500, fontSize: 13 }}>{c.name || '(sin ficha)'}</div>{(r.notes || []).length > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>💬 {(r.notes || []).length} nota{(r.notes || []).length !== 1 ? 's' : ''}</div>}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{p.number || '—'}</td>
                      <td style={{ fontSize: 12.5 }}>{p.company || '—'}</td>
                      <td style={{ fontSize: 12.5 }}>{fmtFecha(r.dueDate)}</td>
                      <td style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{r.assignedTo || '—'}</td>
                      <td><span className={`badge ${st.class}`}>{st.label}</span></td>
                      <td style={{ fontSize: 12 }}>
                        {r.outcome
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 8, height: 8, borderRadius: 4, background: OUTCOMES[r.outcome]?.color, display: 'inline-block' }} />
                              {OUTCOMES[r.outcome]?.label}
                            </span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <RenewalPanel
          renewal={selected}
          onClose={() => setSelected(null)}
          onUpdate={actualizar}
          onAddNote={agregarNota}
        />
      )}

      {reportOpen && currentUser?.role === 'admin' && (
        <ReportModal renewals={delMes} monthLabel={monthLabel(month)} onClose={() => setReportOpen(false)} />
      )}
    </div>
  );
}
